/**
 * Conditional-logic engine shared by the builder (for the "conditional?"
 * badge, not enforcement — the admin always sees every field/page while
 * editing) and FormRenderer (which actually hides/skips based on it).
 * A single `condition` shape drives both fields and pages so one editor
 * (ConditionEditor.jsx) works for either.
 *
 * condition = { enabled: boolean, match: 'all' | 'any', rules: [rule] }
 * rule = { fieldId: string, operator: OPERATOR, value?: string }
 */

export const OPERATORS = [
  { value: 'equals', label: 'is equal to', needsValue: true },
  { value: 'notEquals', label: 'is not equal to', needsValue: true },
  { value: 'contains', label: 'contains', needsValue: true },
  { value: 'notContains', label: 'does not contain', needsValue: true },
  { value: 'isEmpty', label: 'is empty', needsValue: false },
  { value: 'isNotEmpty', label: 'is not empty', needsValue: false },
  { value: 'greaterThan', label: 'is greater than', needsValue: true },
  { value: 'lessThan', label: 'is less than', needsValue: true },
];

function toComparable(value) {
  if (Array.isArray(value)) return value;
  return value === undefined || value === null ? '' : value;
}

function evaluateRule(rule, values) {
  const raw = values[rule.fieldId];
  const current = toComparable(raw);
  const target = rule.value ?? '';

  switch (rule.operator) {
    case 'equals':
      return Array.isArray(current) ? current.includes(target) : String(current) === String(target);
    case 'notEquals':
      return Array.isArray(current) ? !current.includes(target) : String(current) !== String(target);
    case 'contains':
      return Array.isArray(current)
        ? current.includes(target)
        : String(current).toLowerCase().includes(String(target).toLowerCase());
    case 'notContains':
      return Array.isArray(current)
        ? !current.includes(target)
        : !String(current).toLowerCase().includes(String(target).toLowerCase());
    case 'isEmpty':
      return Array.isArray(current) ? current.length === 0 : current === '';
    case 'isNotEmpty':
      return Array.isArray(current) ? current.length > 0 : current !== '';
    case 'greaterThan':
      return Number(current) > Number(target);
    case 'lessThan':
      return Number(current) < Number(target);
    default:
      return true;
  }
}

/** Returns true (visible) when there's no condition, or an empty rule set. */
export function evaluateCondition(condition, values) {
  if (!condition?.enabled || !condition.rules?.length) return true;
  const results = condition.rules.map((rule) => evaluateRule(rule, values));
  return condition.match === 'any' ? results.some(Boolean) : results.every(Boolean);
}

/** All fields across every page a condition rule could reference, minus layout-only and the field itself. */
export function referenceableFields(pages, excludeId) {
  const fields = [];
  pages.forEach((page) => {
    (page.components || []).forEach((c) => {
      if (c.id === excludeId) return;
      if (c.type === 'heading' || c.type === 'paragraph') return;
      fields.push({ id: c.id, label: c.label || c.id, type: c.type });
    });
  });
  return fields;
}
